import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client'; // Import socket.io-client

// Create a single, shared socket instance
// We connect immediately, but it won't be authenticated until login.
const socket = io(process.env.REACT_APP_BACKEND_URL || 'https://voidapi.shigjeta.com/', {
    autoConnect: true,
    transports: ["websocket"]
});

const AuthContext = createContext();

const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      setAuthToken(token);
      
      // Pass the token for socket authentication on reconnect
      socket.auth = { token };
      socket.connect();

      axios.get('/api/auth/me')
        .then(res => {
          const userData = res.data;
          setUser(userData);
          setAuth({ token, user: userData });
        })
        .catch(() => logout())
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await axios.post('/api/auth/login', { email, password });
    
    if (res.data && res.data.token) {
      const { token, ...userData } = res.data;
      
      localStorage.setItem('authToken', token);
      localStorage.setItem('authUser', JSON.stringify(userData));
      
      setAuthToken(token);
      setUser(userData);
      setAuth({ token, user: userData });

      // --- Authenticate the live socket connection ---
      socket.auth = { token };
      socket.disconnect().connect(); // Reconnect with the new auth token

      return userData;
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    setAuthToken(null);
    setUser(null);
    setAuth(null);
    socket.auth = {}; // Clear auth token from socket
    socket.disconnect(); // Disconnect on logout
  };

  // The value provided to all child components
  const value = {
    user,
    auth,
    loading,
    login,
    logout,
    socket, // <-- Provide the live socket instance to the whole app
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  return useContext(AuthContext);
};